import produce from 'immer';
import { useCallback, useMemo } from 'react';

import { EntityLike } from '@work4all/components/lib/components/entity-picker/types';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDeleteEntity } from '@work4all/data';
import { usePermissions } from '@work4all/data/lib/hooks/use-permissions';
import { MAX_LAST_ITEMS_TO_SAVE } from '@work4all/data/lib/hooks/use-search-history';
import { SearchHistoryResults } from '@work4all/data/lib/settings/types';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileType } from '@work4all/models/lib/File';

import { environment } from '../../environments';
import { settings, useSetting } from '../../settings';

import { DELETE_ENTITY_MAX_COUNT } from './constants';

export interface IUseDeleteEntitiesConfigParams<T> {
  entityType: Entities;
  selectedEntities: EntityLike[];
}

export function useDeleteEntitiesToolbarConfig<
  T extends { id?: number | string } = { id?: number | string }
>(params: IUseDeleteEntitiesConfigParams<T>) {
  const { entityType, selectedEntities } = params;

  const { canDelete } = usePermissions();

  const wip = !environment.entities[entityType];

  const [deleteEntity] = useDeleteEntity();

  const canDeleteSelectedEntities =
    selectedEntities?.every((record) => {
      return canDelete({ entity: entityType, record });
    }) ?? false;

  const disabled = wip || !canDeleteSelectedEntities;
  const maxCount = DELETE_ENTITY_MAX_COUNT;

  const { set: setSearchHistory, value: searchHistory } = useSetting(
    settings.searchHistory()
  );

  const handler = useCallback(
    async (ids?: string[]) => {
      const entitiesIds = ids
        ? ids.map((id) => {
            /**
             * When one or multiple groupings are active in the table,
             * the ids are in the format of "54321.98765"
             * the last segment is the id of the entity
             */
            const items = id.toString().split('.');
            return items[items.length - 1];
          })
        : selectedEntities.map(({ id }) => id);

      sendAmplitudeData(EventType.DeleteElement, {
        entryPoint: entityType + 'List',
      });

      await deleteEntity({ type: entityType, ids: entitiesIds });

      if (isFileType(entityType)) {
        switch (entityType) {
          case Entities.customer:
            setSearchHistory(
              filterSearchedItems(FileType.CUSTOMER, entitiesIds, searchHistory)
            );
            break;

          case Entities.supplier:
            setSearchHistory(
              filterSearchedItems(FileType.SUPPLIER, entitiesIds, searchHistory)
            );
            break;

          case Entities.project:
            setSearchHistory(
              filterSearchedItems(FileType.PROJECT, entitiesIds, searchHistory)
            );
            break;
        }
      }
    },
    [
      deleteEntity,
      entityType,
      searchHistory,
      selectedEntities,
      setSearchHistory,
    ]
  );

  return useMemo(
    () => ({ disabled, maxCount, handler }),
    [disabled, maxCount, handler]
  );
}

const isFileType = (entityType: Entities) => {
  return Object.values(FileType).includes(entityType.toString() as FileType);
};

const filterSearchedItems = (
  entityType: FileType,
  ids: (string | number)[],
  value: SearchHistoryResults
) => {
  const filteredItems = produce(value, (draft) => {
    draft[entityType] = draft[entityType]
      .filter((x) => !ids.includes(Number(x.id)))
      .slice(0, MAX_LAST_ITEMS_TO_SAVE);
  });
  return filteredItems;
};
